import { useLanguage } from "@/i18n/LanguageContext";
import LongPage from "@/components/LongPage";

export default function CMMS() {
  const { t } = useLanguage();
  return (
    <LongPage
      breadcrumb={[{ name: "Products", to: "/#modules" }]}
      eyebrow={t("CMMS")}
      title={t("Maintain what's actually hurting production.")}
      seoTitle="CMMS Software — Equipment Passport, PM, MTTR/MTBF — BEEDIGIT"
      seoDescription="Equipment passport, PM plans, work requests, spares — connected to live downtime and EUR cost. Stop maintaining the wrong things. Book a demo."
      description={t("BEEDIGIT CMMS connects maintenance to production reality. Every breakdown ranked by the EUR it cost you. Every PM justified by the failure it prevents. Every spare tied to the asset that needs it. Stop maintaining the loudest machine and start maintaining the most expensive one.")}
      bullets={[
        "Equipment passport — complete history per asset",
        "PM plans driven by usage and real failures",
        "MTTR / MTBF tied to actual production downtime",
        "Maintenance cost vs production loss, on one screen",
      ]}
      schema="product"
      primaryCta={{ label: "Book a 30-min demo", to: "/get-started" }}
      secondaryCta={{ label: "Talk to expert", to: "/get-started" }}
      ctaHeading="Stop maintaining the loudest machine. Maintain the most expensive one."
      ctaBody="Show us your top 5 assets. In 30 minutes we'll show you the downtime they really cost you, the PMs that actually pay back, and the spares you should have stocked."
      sections={[
        {
          kind: "personas",
          eyebrow: "Who it's for",
          heading: "If uptime is your job, this is your tool",
          intro: "BEEDIGIT CMMS ties every maintenance action to the production it protects — so maintenance stops being a cost center and starts being a margin lever.",
          items: [
            { role: "CEO / Owner", pain: "Breakdowns kill the month and nobody can quantify them.", value: "Downtime cost in EUR — and a plan to cut it." },
            { role: "COO", pain: "Asset reliability is wildly different across plants.", value: "Same reliability KPIs, every site — and a benchmark to close the gap." },
            { role: "Maintenance Manager", pain: "PM compliance is high. Breakdowns are too.", value: "PMs ranked by the failures they actually prevent — not by the calendar." },
            { role: "Plant Manager", pain: "I lose a shift to a breakdown and don't know why.", value: "Every breakdown logged, ranked, owned — with a recurrence flag." },
            { role: "Reliability Engineer", pain: "MTTR and MTBF are theoretical numbers in a spreadsheet.", value: "MTTR/MTBF computed from real downtime — by asset, by mode, by shift." },
            { role: "CFO", pain: "Maintenance budget grows. Output doesn't.", value: "Maintenance EUR vs production EUR saved — finally a controllable ratio." },
          ],
        },
        {
          kind: "prose",
          eyebrow: "The problem",
          heading: "Maintenance is busy. Production is still going down.",
          body:
            "In most factories, maintenance and production live in two different systems — and two different realities. Maintenance hits its PM compliance target. Production still loses a shift to a breakdown that wasn't on anyone's risk list. The CMMS knows the work was done. It doesn't know it didn't matter.\n\nMeanwhile, the assets that actually hurt production rarely get the attention they deserve, and the spares for the next breakdown are sitting in someone else's warehouse.\n\nBEEDIGIT CMMS ties every PM, every work order and every spare to the downtime it prevents — so maintenance stops being measured by activity and starts being measured by uptime delivered.",
        },
        {
          kind: "steps",
          eyebrow: "How it works",
          heading: "Reliability tied to reality in three steps",
          intro: "We don't replace your maintenance team. We give them a CMMS that finally knows what production is doing.",
          items: [
            { n: 1, title: "We connect", body: "Every asset gets a passport: specs, history, criticality. Live downtime from Monitoring streams in — by asset, mode and cause." },
            { n: 2, title: "You prioritize", body: "PM plans, work requests and spares are ranked by the EUR they save — not by who shouted loudest." },
            { n: 3, title: "You improve", body: "MTTR/MTBF, repeat-failure flags and PM effectiveness reports land automatically. Reliability stops being a quarterly review and becomes a daily practice." },
          ],
        },
        {
          kind: "features",
          eyebrow: "What you get",
          heading: "A CMMS that earns its keep",
          cols: 3,
          items: [
            { title: "Equipment passport", desc: "Complete record per asset: specs, manuals, history, criticality, BOM of spares." },
            { title: "PM plans", desc: "Time-, usage- or condition-based. Auto-scheduled. Compliance tracked." },
            { title: "Work requests", desc: "Raised from BEEPAD at the line. Routed automatically. Owned with an SLA." },
            { title: "Work orders", desc: "From request to closeout — labor, parts, downtime, root cause, signature." },
            { title: "Spare-parts inventory", desc: "Linked to the asset that consumes it. Stockouts flagged before they bite." },
            { title: "MTTR / MTBF, real", desc: "Computed from actual production downtime — not from a maintenance log." },
            { title: "Failure modes", desc: "Tag breakdowns by cause. Recurrence flagged. Pattern becomes obvious." },
            { title: "PM effectiveness", desc: "Did the PM actually prevent the failure? Now you can tell." },
            { title: "Cost view", desc: "Maintenance EUR vs production EUR lost — per asset, per month." },
          ],
        },
        {
          kind: "callout",
          eyebrow: "Live in weeks",
          heading: "Your first ranked asset list — in 14 days.",
          body: "We stand up the equipment passport for your top 20 assets, wire in live downtime from your machines, and you have a ranked list of where to maintain — by EUR — within two weeks. Scale from there.",
          href: "/platform",
          linkLabel: "See the platform",
        },
        {
          kind: "gains",
          eyebrow: "What it's worth",
          heading: "What every role gets back",
          intro: "Maintenance stops being measured by activity. It gets measured by uptime delivered.",
          items: [
            { role: "CEO", cares: "Output protection.", gain: "Fewer breakdowns where they actually cost you the most.", kpis: "Unplanned downtime EUR, OEE availability.", pitch: "Protect the output you already sell." },
            { role: "COO", cares: "Reliability across plants.", gain: "Same scorecard, every site — close the worst-plant gap.", kpis: "MTBF, MTTR, PM compliance.", pitch: "Make reliability portable across sites." },
            { role: "Maintenance Manager", cares: "Doing the right work.", gain: "PMs ranked by what they actually prevent — and what they cost.", kpis: "PM effectiveness, work-order backlog, MTTR.", pitch: "Spend the wrench where it pays back." },
            { role: "Reliability Engineer", cares: "Failure modes.", gain: "Failure-mode tagging, recurrence flags, FMEA fed by reality.", kpis: "Repeat failures, top failure modes, RPN actuals.", pitch: "Engineer reliability with real data." },
            { role: "Plant Manager", cares: "Hitting plan.", gain: "Every breakdown logged with cause, EUR cost and owner.", kpis: "Unplanned stops, breakdown duration, recurrence.", pitch: "Stop losing the shift to invisible failures." },
            { role: "CFO", cares: "Maintenance ROI.", gain: "Maintenance EUR vs production EUR saved — a controllable ratio.", kpis: "Maintenance cost / output, spare-parts turns.", pitch: "Justify every wrench with a number." },
            { role: "Stores / Supply", cares: "Spares availability.", gain: "Spares linked to asset criticality — stockouts flagged before they bite.", kpis: "Stockout events, spare-parts value, lead time.", pitch: "Stock what actually matters." },
          ],
        },
        {
          kind: "useCases",
          eyebrow: "In the real world",
          heading: "What changes on Monday morning",
          items: [
            { persona: "The operator", scenario: "Notices a vibration. Taps a work request on BEEPAD with one photo and one tag.", outcome: "Maintenance sees it in seconds. Triaged before it becomes a breakdown." },
            { persona: "The maintenance lead", scenario: "Opens the week. Top 5 work orders are ranked by EUR downtime they'll prevent.", outcome: "Spends the wrench where it pays back — not where it shouts." },
            { persona: "The reliability engineer", scenario: "Pulls failure modes for Pump A. Bearing failures up 40% this quarter.", outcome: "Updates PM to add vibration check. Failures drop the following month." },
            { persona: "The plant manager", scenario: "Reviews the breakdown log. Compressor 3 lost 14 hours this month — EUR 9k.", outcome: "Approves the rebuild. Pays back inside the quarter." },
            { persona: "The CFO", scenario: "Asks for the maintenance ROI report. EUR spent vs EUR downtime saved — by asset.", outcome: "Maintenance budget stops being a fight. Becomes a justified investment." },
            { persona: "The storekeeper", scenario: "Critical bearing about to stock out. Flag appears two weeks early.", outcome: "Reorder placed in time. Next breakdown doesn't wait for shipping." },
          ],
        },
        {
          kind: "mockups",
          eyebrow: "See it",
          heading: "Built for the wrench, trusted by the boardroom",
          intro: "Mobile-first for the technician at the asset. Cost-first for the leader signing the budget.",
          items: [
            { alt: "Equipment passport", caption: "Every asset's full life: specs, manuals, history, criticality, spares — in one place." },
            { alt: "PM calendar", caption: "PMs ranked by the failures they actually prevent. Compliance tracked automatically." },
            { alt: "Work-request queue", caption: "Raised at the line on BEEPAD. Routed in seconds. Owned with an SLA." },
            { alt: "MTTR / MTBF dashboard", caption: "Reliability KPIs computed from real downtime — by asset, mode and shift." },
          ],
        },
        {
          kind: "kpi",
          eyebrow: "The numbers that matter",
          heading: "Four KPIs the maintenance team will earn on",
          intro: "Live. Drillable to the asset. Exportable to your ERP and finance.",
          items: [
            { value: "MTBF", label: "Mean time between failures" },
            { value: "MTTR", label: "Mean time to repair" },
            { value: "PM eff.", label: "Did the PM prevent it" },
            { value: "EUR saved", label: "Downtime cost avoided" },
          ],
        },
        {
          kind: "split",
          eyebrow: "Why BEEDIGIT CMMS",
          heading: "What you have today vs what you'll have next month",
          leftTitle: "Today",
          rightTitle: "With BEEDIGIT CMMS",
          left: [
            "PM compliance high, breakdowns also high",
            "Work requests buried in WhatsApp",
            "MTTR/MTBF computed from a maintenance log",
            "Spares stocked by intuition",
            "Maintenance budget defended by tradition",
            "Failure modes lived in someone's head",
          ],
          right: [
            "PMs ranked by failures actually prevented",
            "Work requests raised on BEEPAD, owned in seconds",
            "MTTR/MTBF computed from real production downtime",
            "Spares ranked by asset criticality and lead time",
            "Maintenance EUR vs production EUR saved — on one screen",
            "Failure modes tagged, ranked, fed back into FMEA",
          ],
        },
        {
          kind: "chips",
          eyebrow: "Hardware included",
          heading: "Mobile-first kit for the technician",
          intro: "Tablets, scanners and condition sensors — provisioned and ready.",
          items: [
            "BEEPAD operator / technician tablet",
            "BEEDIGIT HUB",
            "Vibration / temperature sensors",
            "Asset QR labels",
            "GSM · Wi-Fi · Ethernet",
            "Encrypted MQTT over TLS",
          ],
        },
        {
          kind: "chips",
          eyebrow: "Plays well with your stack",
          heading: "Your ERP, your CMMS, your BI",
          intro: "Bi-directional with the maintenance and finance tools you already run on.",
          items: [
            "factory.online", "SAP", "Oracle EBS / Fusion", "Microsoft Dynamics", "Odoo",
            "Power BI", "Tableau", "Data lakes (CSV / JSON / REST)",
            "MQTT", "OPC UA", "REST / Webhooks",
          ],
        },
        {
          kind: "split",
          eyebrow: "Your rollout",
          heading: "From firefighting to ranked in 90 days",
          leftTitle: "Weeks 1–4",
          rightTitle: "Weeks 5–12",
          left: [
            "Equipment passport stood up for top 20 critical assets",
            "Live downtime from Monitoring wired into the asset record",
            "Work-request flow live on BEEPAD — first requests raised",
            "First ranked breakdown list — by EUR cost",
          ],
          right: [
            "PM plans loaded and ranked by failure-prevention impact",
            "Failure-mode tagging tuned with your reliability team",
            "Spares inventory linked to asset criticality",
            "Monthly maintenance ROI report — to the board, not the binder",
          ],
        },
        {
          kind: "callout",
          eyebrow: "Officially certified",
          heading: "The only MES certified by INCIT for raising your SIRI index",
          body: "Asset management and maintenance score in SIRI. BEEDIGIT is the first platform officially recognized for moving the needle on the Smart Industry Readiness Index.",
          href: "/siri",
          linkLabel: "See how BEEDIGIT raises SIRI",
        },
        {
          kind: "faq",
          eyebrow: "FAQ",
          heading: "What buyers ask us first",
          items: [
            { q: "Do we have to migrate from our existing CMMS?", a: "Not necessarily. Many customers start by adding BEEDIGIT alongside their CMMS to get the production-anchored downtime view, then consolidate once they see the value. Either path works." },
            { q: "How fast can we stand up the equipment passport?", a: "Top 20 critical assets typically take 2 weeks from kickoff. Full asset library follows in 60–90 days, scaled by your team's pace." },
            { q: "Where does the downtime data come from?", a: "From BEEDIGIT Monitoring on the same machines. If you're not running Monitoring yet, we can install non-invasive sensors during the CMMS rollout." },
            { q: "Will technicians use it on the floor?", a: "Yes. BEEPAD is designed for the wrench: large touch targets, QR scanning, photo upload, offline-tolerant. Training under an hour. Adoption above 90% in the first month." },
            { q: "Can we do predictive maintenance?", a: "We support condition-based triggers (vibration, temperature, current) out of the box. Full predictive ML is on the roadmap; today we focus on the 80% you can win with condition + failure-mode discipline." },
            { q: "How does PM scheduling work?", a: "Time-based, usage-based (running hours, cycles, output), or condition-based — whichever fits each asset. Calendar generated automatically and compliance tracked." },
            { q: "Will it connect to our ERP and finance?", a: "Out of the box — SAP, Oracle, Microsoft Dynamics, Odoo. Spares consumption, labor and downtime cost flow back to finance via REST/JSON." },
            { q: "What does it cost?", a: "Priced per managed asset, hardware included. Most customers see payback inside two quarters from prevented breakdowns alone. Book the demo — we'll build the business case on your top 20 assets." },
          ],
        },
        {
          kind: "related",
          eyebrow: "Explore the platform",
          heading: "Pairs naturally with",
          items: [
            { title: "Monitoring & OEE", href: "/products/monitoring-oee", desc: "Live machine status feeds the downtime, MTTR and MTBF that drive maintenance priorities." },
            { title: "Manufacturing Execution", href: "/products/manufacturing-execution", desc: "Job-order context for every breakdown — know what was running when it stopped." },
            { title: "Energy Management", href: "/products/energy-management", desc: "Rising SEC is often early warning of asset wear — feed maintenance directly." },
            { title: "Workspace", href: "/products/workspace", desc: "Andon and issue tracking — the coordination layer above every work request." },
          ],
        },
      ]}
    />
  );
}